import React from 'react';
import Reveal from './ui/Reveal';
import ShinyText from './reactbits/ShinyText/ShinyText';

const focusAreas = [
  {
    title: 'Enterprise Software Engineering',
    description:
      'Custom platforms and internal systems designed for scale, maintainability, and long-term product velocity.',
  },
  {
    title: 'AI And Automation',
    description:
      'Practical AI, workflow automation, and integration layers that reduce repetitive work and improve operational clarity.',
  },
  {
    title: 'CMS And Digital Platforms',
    description:
      'Content and experience platforms with clean governance, publishing confidence, and measurable performance.',
  },
  {
    title: 'Product Advisory And Delivery',
    description:
      'Outcome-driven discovery, roadmap shaping, and structured execution from planning through production support.',
  },
];

const deliveryModel = [
  {
    step: '01',
    title: 'Discovery And Alignment',
    text: 'We define business outcomes, user journeys, and technical constraints before implementation starts.',
  },
  {
    step: '02',
    title: 'Architecture And Planning',
    text: 'We establish system boundaries, integration strategy, and delivery milestones to reduce execution risk.',
  },
  {
    step: '03',
    title: 'Build And Validate',
    text: 'Cross-functional teams deliver in short iterations with quality gates, testing, and stakeholder visibility.',
  },
  {
    step: '04',
    title: 'Scale And Operate',
    text: 'We support production readiness, documentation, and improvement cycles to keep systems reliable over time.',
  },
];

const engagementStandards = [
  'Senior engineering ownership from kickoff through release.',
  'Security-aware implementation and production-grade quality checks.',
  'Transparent communication, weekly reporting, and clear accountability.',
  'Maintainable codebases with documentation teams can confidently extend.',
];

const AboutCompanyProfile = () => {
  return (
    <section className="about-profile sec-mar">
      <div className="container">
        <Reveal className="about-profile__hero" delay={0.06}>
          <div className="about-profile__intro">
            <span className="about-profile__eyebrow">Company Profile</span>
            <h2>
              <ShinyText
                text="Engineering Partner For High-Impact Digital Systems"
                className="nalexus-heading-shine"
                color="#f1ede3"
                shineColor="#ffd369"
                speed={2.5}
              />
            </h2>
            <p>
              Nalexus Technologies helps startups and enterprise teams design, build, and scale
              software that performs in real operations. We combine product thinking with disciplined
              engineering delivery so every release is dependable, secure, and easier to evolve.
            </p>
            <div className="about-profile__metrics">
              <div className="metric-chip">
                <strong>Connected</strong>
                <span>Operational Systems</span>
              </div>
              <div className="metric-chip">
                <strong>Practical</strong>
                <span>AI Automation</span>
              </div>
              <div className="metric-chip">
                <strong>Maintainable</strong>
                <span>Product Engineering</span>
              </div>
            </div>
          </div>

          <div className="about-profile__visual">
            <div className="about-profile__vector">
              <svg viewBox="0 0 520 320" role="img" aria-label="Nalexus delivery network">
                <defs>
                  <linearGradient id="nalexus-gold-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffbf34" />
                    <stop offset="100%" stopColor="#a77f21" />
                  </linearGradient>
                </defs>
                <rect x="12" y="12" width="496" height="296" rx="18" />
                <line x1="120" y1="85" x2="260" y2="160" />
                <line x1="260" y1="160" x2="410" y2="90" />
                <line x1="260" y1="160" x2="400" y2="235" />
                <line x1="120" y1="85" x2="110" y2="230" />
                <line x1="110" y1="230" x2="260" y2="160" />
                <circle cx="120" cy="85" r="10" />
                <circle cx="110" cy="230" r="10" />
                <circle cx="260" cy="160" r="12" />
                <circle cx="410" cy="90" r="10" />
                <circle cx="400" cy="235" r="10" />
              </svg>
            </div>
            <p>
              Architecture, delivery, and operations are treated as one connected system, not isolated
              project phases.
            </p>
          </div>
        </Reveal>

        <Reveal className="about-profile__focus" delay={0.08}>
          {focusAreas.map((item) => (
            <article className="focus-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </Reveal>

        <Reveal className="about-profile__delivery" delay={0.1}>
          <div className="delivery-model">
            <h3>How We Deliver</h3>
            <div className="delivery-steps">
              {deliveryModel.map((item) => (
                <article className="delivery-step" key={item.step}>
                  <span className="delivery-step__number">{item.step}</span>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="delivery-standards">
            <h3>Engagement Standards</h3>
            <ul>
              {engagementStandards.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>
        </Reveal>
      </div>
    </section>
  );
};

export default AboutCompanyProfile;
